import React, { useState, useRef, useEffect, useCallback } from 'react';
import './draw-guess.css';

interface DrawGuessGameProps {
  onClose: () => void;
  onOpenLeaderboard: () => void;
}

const GUESS_ROUNDS = [
  {
    image: '/images/generated/player-doodle-lotus-v1.png',
    author: 'Linh Chi',
    answer: 'Bông sen',
    options: ['Hoa mai', 'Bông sen', 'Hoa phượng', 'Hoa cúc'],
  },
  {
    image: '/images/generated/player-doodle-dong-son-drum-v1.png',
    author: 'Bảo Nam',
    answer: 'Trống đồng Đông Sơn',
    options: ['Chuông đồng', 'Trống đồng Đông Sơn', 'Bình gốm', 'Mặt nạ'],
  },
  {
    image: '/images/generated/player-doodle-non-la-v1.png',
    author: 'Ngọc Hà',
    answer: 'Nón lá',
    options: ['Cánh diều', 'Nón lá', 'Chiếc quạt', 'Mái đình'],
  },
];

const PALETTE_COLORS = [
  { color: '#28231f', label: 'Bút màu đen' },
  { color: '#a50021', label: 'Bút màu đỏ' },
  { color: '#246958', label: 'Bút màu xanh' },
  { color: '#e6ba55', label: 'Bút màu vàng' },
];

export const DrawGuessGame: React.FC<DrawGuessGameProps> = ({ onOpenLeaderboard }) => {
  // Activity turn counters
  const [drawTurns, setDrawTurns] = useState<number>(3);
  const [guessTurns, setGuessTurns] = useState<number>(15);
  const [activeTab, setActiveTab] = useState<'draw' | 'guess'>('draw');

  // Drawing state
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef<boolean>(false);
  const [selectedColor, setSelectedColor] = useState<string>('#28231f');
  const [hasInk, setHasInk] = useState<boolean>(false);
  const [drawStatus, setDrawStatus] = useState<string>('Vẽ ít nhất một nét để gửi bài.');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isUploaded, setIsUploaded] = useState<boolean>(false);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const historyRef = useRef<ImageData[]>([]);

  // Guess state
  const [guessIndex, setGuessIndex] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isAnswered, setIsAnswered] = useState<boolean>(false);
  const [guessStatus, setGuessStatus] = useState<string>('Chọn một đáp án.');
  const [guessStatusType, setGuessStatusType] = useState<'normal' | 'success' | 'error'>('normal');

  const currentRound = GUESS_ROUNDS[guessIndex];

  // Exact canvas resolution logic from ref/games/draw-guess.html fitCanvas()
  const fitCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || isDrawingRef.current) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.round(rect.width * ratio);
    const height = Math.round(rect.height * ratio);

    if (width === canvas.width && height === canvas.height) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const ink = ctx.strokeStyle;
    const oldWidth = canvas.width;
    const oldHeight = canvas.height;

    const snapshot = document.createElement('canvas');
    snapshot.width = oldWidth;
    snapshot.height = oldHeight;
    const sCtx = snapshot.getContext('2d');
    if (sCtx) {
      sCtx.drawImage(canvas, 0, 0);
    }

    const scale = Math.min(width / oldWidth, height / oldHeight);
    const dx = (width - oldWidth * scale) / 2;
    const dy = (height - oldHeight * scale) / 2;

    canvas.width = width;
    canvas.height = height;

    if (sCtx) {
      ctx.drawImage(snapshot, dx, dy, oldWidth * scale, oldHeight * scale);
    }

    for (let i = 0; i < historyRef.current.length; i++) {
      if (sCtx) {
        sCtx.putImageData(historyRef.current[i], 0, 0);
        const resized = document.createElement('canvas');
        resized.width = width;
        resized.height = height;
        const rc = resized.getContext('2d');
        if (rc) {
          rc.drawImage(snapshot, dx, dy, oldWidth * scale, oldHeight * scale);
          historyRef.current[i] = rc.getImageData(0, 0, width, height);
        }
      }
    }

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 4 * ratio;
    ctx.strokeStyle = ink || selectedColor;
  }, [selectedColor]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (ctx) {
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 8;
      ctx.strokeStyle = selectedColor;
    }

    const ro = new ResizeObserver(() => fitCanvas());
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [fitCanvas, selectedColor]);

  const getPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) * canvas.width) / rect.width,
      y: ((e.clientY - rect.top) * canvas.height) / rect.height,
    };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    e.preventDefault();
    historyRef.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height));
    if (historyRef.current.length > 20) historyRef.current.shift();

    isDrawingRef.current = true;
    setHasInk(true);
    canvas.setPointerCapture(e.pointerId);

    ctx.strokeStyle = selectedColor;
    const p = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(p.x + 0.01, p.y + 0.01);
    ctx.stroke();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    e.preventDefault();
    const p = getPoint(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const handlePointerUp = () => {
    isDrawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.closePath();
  };

  const handleUndo = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    const state = historyRef.current.pop();
    if (state) {
      ctx.putImageData(state, 0, 0);
      setHasInk(historyRef.current.length > 0);
    } else {
      setDrawStatus('Không còn nét nào để hoàn tác.');
    }
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    historyRef.current = [];
    setHasInk(false);
    setDrawStatus('Khung vẽ đã được làm sạch.');
  };

  const handleSubmitDrawing = () => {
    if (!hasInk) {
      setDrawStatus('Hãy vẽ ít nhất một nét trước khi gửi.');
      return;
    }
    if (drawTurns <= 0) {
      setDrawStatus('Bạn đã hết lượt vẽ.');
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    setIsSubmitting(true);
    setDrawStatus('Đang tải bài vẽ lên hàng chờ đoán…');

    setTimeout(() => {
      setDrawTurns((prev) => Math.max(0, prev - 1));
      setUploadPreview(canvas.toDataURL('image/png'));
      setIsUploaded(true);
      setIsSubmitting(false);
    }, 650);
  };

  const handleDrawAgain = () => {
    handleClear();
    setIsUploaded(false);
    setUploadPreview(null);
    setDrawStatus('Vẽ ít nhất một nét để gửi bài.');
  };

  const handleAnswerGuess = (optionText: string) => {
    if (guessTurns <= 0 || isAnswered) return;

    setGuessTurns((prev) => Math.max(0, prev - 1));
    setIsAnswered(true);
    setSelectedAnswer(optionText);

    const isCorrect = optionText === currentRound.answer;
    if (isCorrect) {
      setGuessStatus('Chính xác!');
      setGuessStatusType('success');
    } else {
      setGuessStatus(`Chưa đúng. Đáp án là ${currentRound.answer}.`);
      setGuessStatusType('error');
    }
  };

  const handleNextGuess = () => {
    setGuessIndex((prev) => (prev + 1) % GUESS_ROUNDS.length);
    setIsAnswered(false);
    setSelectedAnswer(null);
    setGuessStatus('Chọn một đáp án.');
    setGuessStatusType('normal');
  };

  return (
    <div className="draw-game-root">
      <main className="game-page">
        <div className="game-wrap">
          <section className="draw-app">
            {/* Header compact bar */}
            <div className="game-compact-bar">
              <div>
                <p className="game-kicker">VẼ & ĐOÁN</p>
                <h1>Chơi ngay</h1>
              </div>
              <div className="header-actions">
                <button
                  id="show-rank"
                  className="rank-button"
                  type="button"
                  onClick={onOpenLeaderboard}
                >
                  Xem BXH · Top 100
                </button>
                <span id="turn-count" className="counter">
                  {drawTurns} VẼ · {guessTurns} ĐOÁN
                </span>
              </div>
            </div>

            {/* Activity Tabs */}
            <div className="activity-tabs" role="group" aria-label="Chọn hoạt động">
              <button
                type="button"
                data-tab="draw"
                aria-pressed={activeTab === 'draw'}
                onClick={() => setActiveTab('draw')}
              >
                Vẽ <span id="draw-tab-count">{drawTurns}</span>
              </button>
              <button
                type="button"
                data-tab="guess"
                aria-pressed={activeTab === 'guess'}
                onClick={() => setActiveTab('guess')}
              >
                Đoán <span id="guess-tab-count">{guessTurns}</span>
              </button>
            </div>

            {/* TAB 1: VẼ (DRAW) */}
            {activeTab === 'draw' && (
              <section
                className={`activity is-active ${isUploaded ? 'is-uploaded' : ''}`}
                data-activity="draw"
                id="draw-activity"
              >
                <div className="draw-layout">
                  <aside className="brief">
                    <span>CHỦ ĐỀ · 01</span>
                    <h2>Vẽ một bông sen</h2>
                    <div className="palette" aria-label="Chọn màu bút">
                      {PALETTE_COLORS.map((item) => (
                        <button
                          key={item.color}
                          type="button"
                          data-color={item.color}
                          aria-label={item.label}
                          aria-pressed={selectedColor === item.color}
                          style={{ '--ink-color': item.color } as React.CSSProperties}
                          onClick={() => setSelectedColor(item.color)}
                        />
                      ))}
                    </div>
                    <div className="draw-tools">
                      <button id="undo" type="button" onClick={handleUndo}>
                        Hoàn tác
                      </button>
                      <button id="clear" type="button" onClick={handleClear}>
                        Xóa hết
                      </button>
                    </div>
                  </aside>

                  <div className="canvas-shell">
                    <canvas
                      ref={canvasRef}
                      id="canvas"
                      width={900}
                      height={600}
                      aria-label="Vùng vẽ hỗ trợ chuột và cảm ứng"
                      onPointerDown={handlePointerDown}
                      onPointerMove={handlePointerMove}
                      onPointerUp={handlePointerUp}
                      onPointerCancel={handlePointerUp}
                    />
                    <small>Chuột / cảm ứng</small>
                  </div>
                </div>

                <div className="submit">
                  <p id="draw-status" aria-live="polite">
                    {drawStatus}
                  </p>
                  <button
                    id="submit-drawing"
                    className="gf-button"
                    type="button"
                    disabled={isSubmitting || !hasInk || drawTurns <= 0}
                    onClick={handleSubmitDrawing}
                  >
                    {isSubmitting ? 'Đang gửi…' : 'Gửi bài vẽ'}
                  </button>
                </div>

                {isUploaded && (
                  <section
                    id="upload-success"
                    className="upload-success"
                    aria-labelledby="upload-title"
                  >
                    {uploadPreview && (
                      <img
                        id="upload-preview"
                        src={uploadPreview}
                        alt="Bản xem trước bài vẽ vừa gửi"
                      />
                    )}
                    <div>
                      <span className="success-mark" aria-hidden="true">
                        ✓
                      </span>
                      <p className="game-kicker">ĐÃ GỬI THÀNH CÔNG</p>
                      <h2 id="upload-title" tabIndex={-1}>
                        Bài vẽ đã vào lượt chờ đoán
                      </h2>
                      <p>
                        Lượt vẽ đã được cập nhật. Bạn có thể vẽ tiếp hoặc chuyển sang
                        đoán tranh của người chơi khác.
                      </p>
                      <div className="success-actions">
                        <button
                          id="draw-again"
                          className="gf-button gf-button--secondary"
                          type="button"
                          onClick={handleDrawAgain}
                        >
                          Vẽ bài khác
                        </button>
                        <button
                          id="go-guess"
                          className="gf-button"
                          type="button"
                          onClick={() => {
                            setIsUploaded(false);
                            setActiveTab('guess');
                          }}
                        >
                          Sang lượt đoán →
                        </button>
                      </div>
                    </div>
                  </section>
                )}
              </section>
            )}

            {/* TAB 2: ĐOÁN (GUESS) */}
            {activeTab === 'guess' && (
              <section className="activity is-active" data-activity="guess">
                <div className="guess-layout">
                  <figure className="guess-art">
                    <img
                      id="guess-image"
                      src={currentRound.image}
                      alt={`Bài vẽ doodle ${currentRound.answer} của người chơi khác`}
                    />
                    <figcaption id="guess-author">
                      Bài vẽ của {currentRound.author}
                    </figcaption>
                  </figure>
                  <div className="guess-copy">
                    <span id="guess-round">
                      LƯỢT ĐOÁN · {guessIndex + 1} / {GUESS_ROUNDS.length}
                    </span>
                    <h2 id="guess-question">Người chơi khác đang vẽ gì?</h2>
                    <div id="guess-options" className="guess-options">
                      {currentRound.options.map((optionText, i) => {
                        const prefix = String.fromCharCode(65 + i);
                        const isCorrect = optionText === currentRound.answer;
                        const isSelected = optionText === selectedAnswer;

                        let cls = '';
                        if (isAnswered) {
                          if (isCorrect) cls = 'correct';
                          else if (isSelected) cls = 'wrong';
                        }

                        return (
                          <button
                            key={optionText}
                            type="button"
                            disabled={isAnswered || guessTurns <= 0}
                            className={cls}
                            onClick={() => handleAnswerGuess(optionText)}
                          >
                            {prefix}. {optionText}
                          </button>
                        );
                      })}
                    </div>
                    <p
                      id="guess-status"
                      className={`feedback ${
                        guessStatusType === 'success'
                          ? 'success'
                          : guessStatusType === 'error'
                          ? 'error'
                          : ''
                      }`}
                      aria-live="polite"
                    >
                      {guessStatus}
                    </p>
                    {isAnswered && (
                      <button
                        id="next-guess"
                        className="gf-button guess-next"
                        type="button"
                        onClick={handleNextGuess}
                      >
                        Câu tiếp theo →
                      </button>
                    )}
                  </div>
                </div>
              </section>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};
